import { useTheme } from "@mui/material/styles";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import type { ApexOptions } from "apexcharts";

import dates from "core/dates";
import numbers from "core/numbers";
import { useTimer } from "core/hooks/useTimer";

import Box from "ui/atom/Box";
import Chart from "ui/Chart";

import { useDAU, useFloat, useMAU, useMonetizedTraffic, useRevenue, useTotalAccounts } from "../fabric";


type CardProps = {
	title: string;
	value?: number;
	diff?: number;
	lastUpdated?: string;
	loading?: boolean;
	refetch?: () => void;
	children?: React.ReactNode;
};

const LastUpdated = ({ lastUpdated }: { lastUpdated?: string }) => {
	useTimer();

	return (
		<Box.Column sx={{ alignItems: "flex-end" }}>
			<Typography variant="caption" color="text.secondary">Last updated:</Typography>
			<Typography variant="caption" color="text.secondary">{dates.seconds.dynamic(new Date(), lastUpdated)?.value} ago</Typography>
		</Box.Column>
	);
};

Dashboard.Card = ({ title, value, diff, lastUpdated, loading, children }: CardProps) => {
	const styles = {
		card: {
			justifyContent: "space-between",
			borderRadius: "12px",
			padding: "16px",
			border: "1px solid #dbe3ee",
			background: "linear-gradient(180deg, #ffffff 0%, #f3f7fb 100%)",
			boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.6)"
		},
		header: {
			justifyContent: "space-between",
			alignItems: "flex-start"
		},
		refresh: {
			alignItems: "center"
		},
		value: {
			fontSize: "24px",
			fontWeight: 700
		},
		subvalue: {
			fontSize: "12px",
			fontWeight: 600,
			textTransform: "uppercase"
		}
	} as SxProps;

	return (
		<Box.Column sx={styles.card}>
			<Box gap={2} sx={styles.header}>
				<Box.Column gap={0.5} sx={{ mb: "auto" }}>
					<Typography variant="caption" color="text.secondary">{title}</Typography>
					{loading
						? <Skeleton variant="text" width={120} height={44} />
						: <Typography variant="h2" color="secondary.main">{numbers.format(value)}</Typography>
					}
					{loading
						? <Skeleton variant="text" width={74} height={18} />
						: diff !== undefined && <Typography color={diff > 0 ? "success" : "error"} sx={styles.subvalue}>{numbers.format(diff, "percentage", { decimals: 2 })}</Typography>
					}
				</Box.Column>
				{loading
					? (
						<Box.Column sx={{ alignItems: "flex-end" }}>
							<Skeleton variant="text" width={86} height={16} />
							<Skeleton variant="text" width={72} height={16} />
						</Box.Column>
					)
					: <LastUpdated lastUpdated={lastUpdated} />
				}
			</Box>
			{loading
				? <Skeleton variant="rounded" height={190} sx={{ mt: 1 }} />
				: children
			}
		</Box.Column>
	);
};

Dashboard.DAU = () => {
	const dau = useDAU();
	const theme = useTheme();

	const options = {
		xaxis: {
			type: "numeric",
			tickAmount: 5,
			min: dau.min,
			max: dau.max,
			labels: { formatter: dau.formatter }
		},
		colors: ["#9fb3bd", theme.palette.secondary.main]
	} satisfies ApexOptions;

	return (
		<Dashboard.Card
			title="DAU (Today)"
			value={dau.data?.count}
			diff={dau.data?.diff}
			lastUpdated={dau.data?.last}
			loading={dau.isLoading}
		>
			{dau.isSuccess && <Chart.Line series={dau.data.series} options={options} />}
		</Dashboard.Card>
	);
};

Dashboard.MAU = () => {
	const mau = useMAU();
	const theme = useTheme();

	return (
		<Dashboard.Card title="MAU (Rolling 30d)" value={mau.data?.count} lastUpdated={mau.data?.last} loading={mau.isLoading}>
			{mau.isSuccess && <Chart.Line series={mau.data?.series} options={{ colors: [theme.palette.secondary.main] }} />}
		</Dashboard.Card>
	);
};

Dashboard.TA = () => {
	const ta = useTotalAccounts();
	const theme = useTheme();

	return (
		<Dashboard.Card title="Total Accounts" value={ta.data?.count} lastUpdated={ta.data?.last} loading={ta.isLoading}>
			{ta.isSuccess && <Chart.Bar series={ta.data?.series} options={{ colors: ["#9fb3bd", theme.palette.success.main] }} />}
		</Dashboard.Card>
	);
};

Dashboard.Revenue = () => {
	const revenue = useRevenue();
	const theme = useTheme();

	const options = {
		xaxis: {
			type: "numeric",
			tickAmount: 5,
			min: revenue.min,
			max: revenue.max,
			labels: { formatter: revenue.formatter }
		},
		colors: ["#9fb3bd", theme.palette.secondary.main],
		yaxis: {
			min: 0
		},
		tooltip: {
			y: { formatter: (e) => numbers.format(e, "currency", { decimals: 2 }) }
		}
	} satisfies ApexOptions;

	return (
		<Dashboard.Card
			title="Revenue (Today)"
			value={revenue.data?.count}
			lastUpdated={revenue.data?.last}
			loading={revenue.isLoading}
			refetch={revenue.refetch}
		>
			{revenue.isSuccess && <Chart.Line series={revenue.data.series} options={options} yFormat="currency" />}
		</Dashboard.Card>
	);
};

Dashboard.Float = () => {
	const float = useFloat();

	return (
		<Dashboard.Card title="Outstanding Float" lastUpdated={float.data?.last} loading={float.isLoading}>
			{float.isSuccess && <Chart.Gauge
				value={float.data?.count.value}
				min={float.data?.count.min}
				max={float.data?.count.max}
			/>}
		</Dashboard.Card>
	);
};

Dashboard.MonetizedTraffic = () => {
	const traffic = useMonetizedTraffic();

	return (
		<Dashboard.Card title="Monetized Traffic" lastUpdated={traffic.data?.last} loading={traffic.isLoading}>
			{traffic.isSuccess && <Chart.Gauge
				format="percentage"
				value={traffic.data?.count.value}
				min={traffic.data?.count.min}
				max={traffic.data?.count.max}
			/>}
		</Dashboard.Card>
	);
};

export default function Dashboard() {
	const styles = {
		root: {
			display: "grid",
			gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))"
		}
	} as SxProps;

	return (
		<Box gap={1.5} sx={styles.root}>
			<Dashboard.DAU />
			<Dashboard.MAU />
			<Dashboard.TA />
			<Dashboard.Revenue />
			<Dashboard.Float />
			<Dashboard.MonetizedTraffic />
		</Box>
	);
}